// src/services/audioNotificationService.js
import axios from 'axios';

class AudioNotificationService {
  constructor() {
    this.audioCache = new Map();
    this.isEnabled = true;
    this.volume = 0.8;
    this.loadUserPreferences();
  }

  // Load user's audio notification preferences
  async loadUserPreferences() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/user/profile');
      const user = response.data;
      
      // Check if user has audio notifications enabled
      this.isEnabled = user.audioNotifications !== false;
      
      console.log('🔊 Audio notifications enabled:', this.isEnabled);
    } catch (error) {
      console.error('Error loading audio preferences:', error);
      // Default to enabled if we can't load preferences
      this.isEnabled = true;
    }
  }

  // Play audio for specific notification types
  async playAudioForNotification(type, message = '') {
    if (!this.isEnabled) {
      console.log('🔇 Audio notifications disabled by user');
      return;
    }

    try {
      let functionId;
      
      // Map notification types to audio function IDs
      switch (type) {
        case 'success':
          functionId = 'job_completed';
          break;
        case 'error':
          functionId = 'system_error';
          break;
        case 'warning':
          functionId = 'diary_reminder';
          break;
        case 'info':
          // Check message content for specific audio types
          if (message.includes('new job') || message.includes('job request')) {
            functionId = 'new_job_general';
          } else if (message.includes('location')) {
            functionId = 'location_update';
          } else {
            functionId = 'booking_confirmation';
          }
          break;
        default:
          functionId = 'booking_confirmation';
      }

      // Get mapped audio file
      const audioFile = await this.getAudioForFunction(functionId);
      if (audioFile) {
        await this.playAudio(audioFile);
      } else {
        // Fallback to default audio
        await this.playDefaultAudio(type);
      }
    } catch (error) {
      console.error('Error playing audio notification:', error);
      // Fallback to default audio
      await this.playDefaultAudio(type);
    }
  }

  // Get audio file for specific function
  async getAudioForFunction(functionId) {
    try {
      console.log(`🔍 Looking up audio mapping for function: ${functionId}`);
      const response = await axios.get(`/admin/audio-mappings/function/${functionId}`);
      console.log(`🔍 Retrieved audio mapping for ${functionId}:`, response.data);
      
      if (response.data.audioFile) {
        // Check if we need to update the URL path for compatibility
        const audioFile = response.data.audioFile;
        if (audioFile.url && (audioFile.url.startsWith('/api/uploads/audio/') || audioFile.url.startsWith('/uploads/audio/'))) {
          audioFile.url = audioFile.url.replace('/api/uploads/audio/', '/audio/').replace('/uploads/audio/', '/audio/');
          console.log('🔧 Updated audio URL for static serving:', audioFile.url);
        }
        console.log(`✅ Found mapped audio for ${functionId}:`, audioFile.filename);
        return audioFile;
      }
      console.log(`❌ No audioFile object found in response for ${functionId}`);
      return null;
    } catch (error) {
      console.log(`❌ No mapped audio found for ${functionId}:`, error.response?.data?.error || error.message);
      return null;
    }
  }

  // Play specific audio file
  async playAudio(audioFile) {
    try {
      // Handle different URL formats
      let audioUrl = audioFile.url;
      
      // Convert URLs to use static `/audio/` paths
      if (audioUrl && (audioUrl.startsWith('/uploads/audio/') || audioUrl.startsWith('/api/uploads/audio/'))) {
        // Convert to /audio path for static serving
        audioUrl = audioUrl.replace(/^\/uploads\/audio\//, '/audio/').replace(/^\/api\/uploads\/audio\//, '/audio/');
        console.log('🔧 Audio URL corrected to /audio/ path:', audioUrl);
      } else if (!audioUrl.startsWith('http') && !audioUrl.startsWith('/audio/') && audioFile.filename) {
        audioUrl = `/audio/${audioFile.filename}`;
        console.log('🔧 Audio URL constructed to static /audio/ path:', audioUrl);
      } else if (!audioUrl || audioUrl === '') {
        audioUrl = `/audio/${audioFile.filename}`;
        console.log('🔧 Audio URL fallback to static path:', audioUrl);
      }
      
      // Add timestamp to prevent caching issues
      const finalUrl = `${audioUrl}?t=${Date.now()}`;
      console.log('🔊 Playing audio from URL:', finalUrl);
      console.log('📋 File Info:', { 
        originalName: audioFile.originalName, 
        filename: audioFile.filename,
        url: audioFile.url,
        finalUrl 
      });
      
      const audio = new Audio(finalUrl);
      audio.volume = this.volume;
      
      // Add detailed error handling for debugging
      audio.addEventListener('error', (e) => {
        console.error(`❌ Failed to load audio file: ${finalUrl}`, e);
        console.error('🔍 File may be missing due to Heroku ephemeral filesystem');
      });
      
      audio.addEventListener('canplaythrough', () => {
        console.log('✅ Audio loaded successfully');
      });
      
      // Add to cache for future use
      this.audioCache.set(audioFile.url, audio);
      
      await audio.play();
      console.log(`🔊 Playing audio: ${audioFile.originalName} from ${finalUrl}`);
    } catch (error) {
      console.error('❌ Error playing audio:', error);
      console.error('💡 This likely indicates file persistence issue on Heroku');
      throw error;
    }
  }

  // Play default ping noise for all notification fallbacks
  async playDefaultAudio(type) {
    try {
      console.log(`🔊 Playing ping notification for: ${type}`);
      
      // Use the existing alert sound functionality from public/alert-sound.js
      if (typeof window.createAlertSound === 'function') {
        // Call the existing alert sound generator
        window.createAlertSound();
        console.log('🔔 Ping notification sound played successfully');
      } else {
        // Fallback to simple audio ping if alert-sound.js not loaded
        await this.playPingNotification();
      }
    } catch (error) {
      console.error('Error playing ping notification:', error);
      // Absolute fallback: try playing ping notification
      try {
        await this.playPingNotification();
      } catch (fallbackError) {
        console.error('Even ping notification failed:', fallbackError);
      }
    }
  }

  // Play simple ping notification as fallback
  async playPingNotification() {
    try {
      // Create a simple beep using Web Audio API if not available
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configure a short 800Hz ping
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3 * this.volume, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      
      console.log('🔔 Generated ping notification sound');
    } catch (error) {
      console.error('Failed to generate ping notification:', error);
    }
  }

  // Play audio for new job notifications
  async playNewJobAudio(jobSource = 'general') {
    if (!this.isEnabled) return;

    try {
      let functionId;
      switch (jobSource) {
        case 'pool':
          functionId = 'new_job_pool';
          break;
        case 'mylink':
          functionId = 'new_job_mylink';
          break;
        default:
          functionId = 'new_job_general';
      }

      const audioFile = await this.getAudioForFunction(functionId);
      if (audioFile) {
        await this.playAudio(audioFile);
      } else {
        // Fallback to default new job audio
        const audioUrl = `/NewJob_MyLink.mp3?t=${Date.now()}`;
        const audio = new Audio(audioUrl);
        audio.volume = this.volume;
        
        audio.addEventListener('error', (e) => {
          console.error('Failed to load fallback new job audio:', e);
        });
        
        await audio.play();
        console.log('🔊 Playing fallback new job audio');
      }
    } catch (error) {
      console.error('Error playing new job audio:', error);
    }
  }

  // Play audio for job accepted notifications
  async playJobAcceptedAudio(jobSource = 'general') {
    if (!this.isEnabled) return;

    try {
      let functionId;
      switch (jobSource) {
        case 'pool':
          functionId = 'job_accepted_pool';
          break;
        case 'mylink':
          functionId = 'job_accepted_mylink';
          break;
        default:
          functionId = 'job_accepted_general';
      }

      const audioFile = await this.getAudioForFunction(functionId);
      if (audioFile) {
        await this.playAudio(audioFile);
      } else {
        // Fallback to default success audio
        const audioUrl = `/NewJob_MyLink.mp3?t=${Date.now()}`;
        const audio = new Audio(audioUrl);
        audio.volume = this.volume;
        
        audio.addEventListener('error', (e) => {
          console.error('Failed to load fallback job accepted audio:', e);
        });
        
        await audio.play();
        console.log('🔊 Playing fallback job accepted audio');
      }
    } catch (error) {
      console.error('Error playing job accepted audio:', error);
    }
  }

  // Enable/disable audio notifications
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log('🔊 Audio notifications:', enabled ? 'enabled' : 'disabled');
  }

  // Set volume (0.0 to 1.0)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    console.log('🔊 Audio volume set to:', this.volume);
  }

  // Play audio for Stop/Go (Running Man/Standing Man) notifications
  async playStopGoAudio(isWorking) {
    if (!this.isEnabled) {
      console.log('🔇 Audio notifications disabled - skipping Stop/Go audio');
      return;
    }

    try {
      if (isWorking) {
        console.log('🏃 Attempting to get Running Man audio mapping...');
        // Starting (Running Man) - try to get mapped audio
        const runningManAudio = await this.getAudioForFunction('running_man_start');
        if (runningManAudio) {
          await this.playAudio(runningManAudio);
          console.log('🔊 Playing Running Man (Starting) mapped audio');
        } else {
          // Try alternative Online/Driver Online mappings
          const driverOnlineAudio = await this.getAudioForFunction('driver_online');
          if (driverOnlineAudio) {
            await this.playAudio(driverOnlineAudio);
            console.log('🔊 Playing Driver Online mapped audio');
          } else {
            // Fallback to success notification audio
            await this.playAudioForNotification('success', 'Running Man - Location tracking started');
            console.log('🔊 Playing Running Man (Starting) fallback audio');
          }
        }
      } else {
        console.log('🚶 Attempting to get Standing Man audio mapping...');
        // Stopping (Standing Man) - try to get mapped audio
        const standingManAudio = await this.getAudioForFunction('standing_man_stop');
        if (standingManAudio) {
          await this.playAudio(standingManAudio);
          console.log('🔊 Playing Standing Man (Stopping) mapped audio');
        } else {
          // Try alternative Offline/Driver Offline mappings
          const driverOfflineAudio = await this.getAudioForFunction('driver_offline');
          if (driverOfflineAudio) {
            await this.playAudio(driverOfflineAudio);
            console.log('🔊 Playing Driver Offline mapped audio');
          } else {
            // Fallback to warning notification audio
            await this.playAudioForNotification('warning', 'Standing Man - Location tracking stopped');
            console.log('🔊 Playing Standing Man (Stopping) fallback audio');
          }
        }
      }
    } catch (error) {
      console.error('Error playing Stop/Go audio:', error);
      // Fallback to default audio
      if (isWorking) {
        await this.playDefaultAudio('success');
      } else {
        await this.playDefaultAudio('warning');
      }
    }
  }

  // Test audio notifications
  async testAudio(type = 'info') {
    console.log(`🧪 Testing audio notification for type: ${type}`);
    await this.playAudioForNotification(type, 'Test notification');
  }

  // Test if audio files are accessible
  async testAudioFiles() {
    const testFiles = [
      '/NewJob_MyLink.mp3',
      '/NewJob_Pool.mp3', 
      '/Re-Open.mp3'
    ];

    const results = [];
    
    for (const file of testFiles) {
      try {
        const audio = new Audio(`${file}?t=${Date.now()}`);
        
        return new Promise((resolve) => {
          audio.addEventListener('canplaythrough', () => {
            console.log(`✅ Audio file accessible: ${file}`);
            results.push({ file, accessible: true });
            resolve();
          });
          
          audio.addEventListener('error', (e) => {
            console.error(`❌ Audio file not accessible: ${file}`, e);
            results.push({ file, accessible: false, error: e.message });
            resolve();
          });
          
          // Set a timeout
          setTimeout(() => {
            console.error(`⏰ Audio file timeout: ${file}`);
            results.push({ file, accessible: false, error: 'Timeout' });
            resolve();
          }, 5000);
        });
      } catch (error) {
        console.error(`❌ Error testing audio file: ${file}`, error);
        results.push({ file, accessible: false, error: error.message });
      }
    }
    
    return results;
  }
}

// Create singleton instance
const audioNotificationService = new AudioNotificationService();

export default audioNotificationService;
